import {
  useMeterStatus,
  useMeterType,
  useReadingStatus,
} from "@features/mx/routeManagement/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { MODAL_PAGE_SIZE } from "@shared/utils/constants";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

export const ResourcesTable = ({ meterData }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch dropdown options
  const { data: readingStatusData } = useReadingStatus();
  const { data: meterTypeData } = useMeterType();
  const { data: meterStatusData } = useMeterStatus();

  // Extract filters from URL
  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentFilterStatus = searchParams.get("filter_status") || "all";
  const currentReadingStatus = searchParams.get("reading_status") || "all";
  const currentMeterType = searchParams.get("meter_type") || "all";

  const [pendingFilters, setPendingFilters] = useState({
    filterStatus: currentFilterStatus,
    readingStatus: currentReadingStatus,
    meterType: currentMeterType,
  });

  const columns: TableColumn<any>[] = [
    {
      key: "meterNumber",
      header: "Resource ID",
    },
    {
      key: "deviceNo",
      header: "Serial No",
    },
    {
      key: "productCode",
      header: "Utility Service",
      headerTooltip:
        "Account holder or service recipient. Links meter readings to billing records.",
    },
    {
      key: "resourceTypeDisplay",
      header: "Type",
    },
    {
      key: "resourceSubtypeDisplay",
      header: "Sub Type",
    },
    {
      key: "premiseName",
      header: "Premise",
    },
    {
      key: "unit",
      header: "Unit",
    },
  ];

  const pagination = meterData
    ? {
      currentPage,
      totalPages: Math.ceil(meterData.count / MODAL_PAGE_SIZE),
      totalItems: meterData.count,
      startItem: (currentPage - 1) * MODAL_PAGE_SIZE + 1,
      endItem: Math.min(currentPage * MODAL_PAGE_SIZE, meterData.count),
      itemsPerPage: MODAL_PAGE_SIZE,
    }
    : undefined;

  const handlePendingFilterChange = (
    key: keyof typeof pendingFilters,
    value: string
  ) => {
    setPendingFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      filter_status: pendingFilters.filterStatus,
      reading_status: pendingFilters.readingStatus,
      meter_type: pendingFilters.meterType,
      page: 1,
    };
    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };
    delete newParams.filter_status;
    delete newParams.reading_status;
    delete newParams.meter_type;
    delete newParams.search;
    newParams.page = 1;
    setSearchParams(newParams);
    setPendingFilters({
      filterStatus: "all",
      readingStatus: "all",
      meterType: "all",
    });
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      filter_status: currentFilterStatus,
      reading_status: currentReadingStatus,
      meter_type: currentMeterType,
    };
    if (searchTerm.trim()) newParams.search = searchTerm.trim();
    else delete newParams.search;
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  const filterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">All Status</label>
          <Select
            value={pendingFilters.filterStatus}
            onValueChange={(value) =>
              handlePendingFilterChange("filterStatus", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {meterStatusData?.result?.map((status) => (
                <SelectItem key={status.key} value={String(status.key)}>
                  {status.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            All Reading Status
          </label>
          <Select
            value={pendingFilters.readingStatus}
            onValueChange={(value) =>
              handlePendingFilterChange("readingStatus", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select reading status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reading Status</SelectItem>
              {readingStatusData?.result?.map((status) => (
                <SelectItem key={status.key} value={String(status.key)}>
                  {status.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">All Types</label>
          <Select
            value={pendingFilters.meterType}
            onValueChange={(value) =>
              handlePendingFilterChange("meterType", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select meter type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {meterTypeData?.result?.map((type) => (
                <SelectItem key={type.value} value={String(type.key)}>
                  {type.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={handleResetFilters}>
          Clear
        </Button>
      </div>
    </div>
  );
  return (
    <div className="pt-2">
      <GenericTable
        data={meterData?.results || []}
        columns={columns}
        rowKey="meterNumber"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search resources..."
        emptyMessage="No resources found"
        filterPanel={filterPanel}
      />
    </div>
  );
};
